import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import type { MulticamAngle, MulticamAnnotation } from '../../graphqlService';
import { DEFAULT_CATEGORY_ID } from '../../utils/annotationCategories';
import { formatDuration, formatTimecode, parseTimecode } from '../../utils/timecode';
import CategorySelect from '../AnnotationStudio/CategorySelect';
import {
  ALL_ANGLES,
  AnnotationDraft,
  AnnotationKind,
  CUT_CATEGORY,
  angleName,
  findAngle,
  isCut,
} from './multicamStudioModel';
import { angleColor } from './multicamTheme';

interface MulticamAnnotationDialogProps {
  open: boolean;
  annotation: MulticamAnnotation | null;
  angles: MulticamAngle[];
  fps: number;
  /** Length of the shared timeline in seconds; 0 when unknown. */
  duration: number;
  getPlayhead: () => number;
  hasFootage: (angleKey: string, seconds: number) => boolean;
  saving?: boolean;
  /** Why the last save failed, in plain words. */
  error?: string | null;
  onCancel: () => void;
  onSave: (draft: AnnotationDraft) => void;
}

const TC_INPUT_SX = { fontVariantNumeric: 'tabular-nums', fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace' } as const;

/**
 * Edit a note or a camera cut: what it is, the angle it cuts to (or is
 * about), its text, category and exact timing. Times are typed as timecode
 * (HH:MM:SS:FF), m:ss or seconds, or taken from the playhead; a time left as
 * shown keeps its exact stored value, so editing only the text never moves it.
 */
const MulticamAnnotationDialog: React.FC<MulticamAnnotationDialogProps> = ({
  open,
  annotation,
  angles,
  fps,
  duration,
  getPlayhead,
  hasFootage,
  saving = false,
  error = null,
  onCancel,
  onSave,
}) => {
  const [kind, setKind] = useState<AnnotationKind>('NOTE');
  const [text, setText] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY_ID);
  const [scope, setScope] = useState<string>(ALL_ANGLES);
  const [cutTarget, setCutTarget] = useState<string | null>(null);
  const [startText, setStartText] = useState('');
  const [endText, setEndText] = useState('');
  const [isRange, setIsRange] = useState(false);
  const [initial, setInitial] = useState({ start: '', end: '' });
  const [validation, setValidation] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !annotation) return;
    const cut = isCut(annotation);
    const time = annotation.timelineTimeMs / 1000;
    const end = typeof annotation.endTimeMs === 'number' && annotation.endTimeMs > annotation.timelineTimeMs
      ? annotation.endTimeMs / 1000
      : null;
    const start = formatTimecode(time, fps);
    const endShown = formatTimecode(end ?? time + 2, fps);
    setKind(cut ? 'SWITCH' : 'NOTE');
    setText(annotation.text);
    setCategory(!cut && annotation.category ? annotation.category : DEFAULT_CATEGORY_ID);
    setScope(!cut && annotation.targetAngleKey && findAngle(angles, annotation.targetAngleKey) ? annotation.targetAngleKey : ALL_ANGLES);
    setCutTarget(annotation.targetAngleKey ?? angles[0]?.angleKey ?? null);
    setStartText(start);
    setEndText(endShown);
    setIsRange(!cut && end !== null);
    setInitial({ start, end: endShown });
    setValidation(null);
    // Only when a different annotation is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, annotation?.id]);

  if (!annotation) {
    return <Dialog open={false} onClose={onCancel} />;
  }

  const originalTime = annotation.timelineTimeMs / 1000;
  const originalEnd = typeof annotation.endTimeMs === 'number' && annotation.endTimeMs > annotation.timelineTimeMs
    ? annotation.endTimeMs / 1000
    : null;
  // A time left as it was shown keeps its exact stored value.
  const start = startText === initial.start ? originalTime : parseTimecode(startText, fps);
  const end = isRange ? (endText === initial.end && originalEnd !== null ? originalEnd : parseTimecode(endText, fps)) : null;
  const limit = duration > 0 ? duration + 1e-6 : Number.POSITIVE_INFINITY;
  const startInvalid = start === null || start > limit;
  const endInvalid = isRange && (end === null || end > limit || (start !== null && end <= start));
  const cutting = kind === 'SWITCH';
  const targetAngle = findAngle(angles, cutTarget);
  const footageWarning = cutting && targetAngle && start !== null && !hasFootage(targetAngle.angleKey, start)
    ? `${angleName(targetAngle)} has no footage at this time, so the cut would show nothing.`
    : null;

  const handleSave = () => {
    if (!cutting && !text.trim()) {
      setValidation('Write the note before saving.');
      return;
    }
    if (cutting && !targetAngle) {
      setValidation('Choose the angle to cut to.');
      return;
    }
    if (startInvalid) {
      setValidation(duration > 0
        ? `Enter a time between 00:00:00:00 and ${formatTimecode(duration, fps)}.`
        : 'Enter a valid time.');
      return;
    }
    if (endInvalid) {
      setValidation('The end must be after the start and within the set.');
      return;
    }
    setValidation(null);
    const time = start as number;
    onSave({
      type: kind,
      time,
      // Cuts keep a stored end (from older notes) while it still follows the cut.
      endTime: cutting ? (originalEnd !== null && originalEnd > time ? originalEnd : null) : end,
      text: text.trim(),
      category: cutting ? CUT_CATEGORY : category,
      targetAngleKey: cutting ? (cutTarget as string) : scope === ALL_ANGLES ? null : scope,
      storedColor: annotation.color,
    });
  };

  const helper = (value: number | null, invalid: boolean) =>
    invalid ? 'HH:MM:SS:FF, m:ss or seconds' : value !== null ? `${value.toFixed(2)}s` : ' ';

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-multicam-annotation-title"
    >
      <DialogTitle id="edit-multicam-annotation-title" sx={{ fontWeight: 700 }}>
        {isCut(annotation) ? 'Edit cut' : 'Edit note'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, value: AnnotationKind | null) => value && setKind(value)}
            aria-label="Type"
          >
            <ToggleButton value="NOTE" sx={{ gap: 0.75 }}>
              <ChatBubbleOutlineOutlinedIcon fontSize="small" />
              Note
            </ToggleButton>
            <ToggleButton value="SWITCH" sx={{ gap: 0.75 }} disabled={angles.length < 2 && !isCut(annotation)}>
              <ContentCutOutlinedIcon fontSize="small" />
              Camera cut
            </ToggleButton>
          </ToggleButtonGroup>

          {cutting ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }} id="edit-cut-target-label">
                Cut to
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={cutTarget}
                onChange={(_, value: string | null) => value && setCutTarget(value)}
                aria-labelledby="edit-cut-target-label"
                sx={{ flexWrap: 'wrap' }}
              >
                {angles.map((angle) => (
                  <ToggleButton key={angle.angleKey} value={angle.angleKey} sx={{ gap: 0.75, fontWeight: 600 }}>
                    <Box component="span" aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: angleColor(angle.angleKey) }} />
                    {angleName(angle)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {footageWarning && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  {footageWarning}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <CategorySelect value={category} onChange={setCategory} minWidth={200} />
              <TextField
                select
                size="small"
                label="Applies to"
                value={scope}
                onChange={(event) => setScope(String(event.target.value))}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={ALL_ANGLES}>All angles</MenuItem>
                {angles.map((angle) => (
                  <MenuItem key={angle.angleKey} value={angle.angleKey}>
                    {angleName(angle)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <TextField
            label={cutting ? 'Reason (optional)' : 'Note'}
            multiline
            minRows={cutting ? 2 : 3}
            fullWidth
            value={text}
            onChange={(event) => setText(event.target.value)}
          />

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label={isRange ? 'In' : 'Time'}
              size="small"
              value={startText}
              onChange={(event) => setStartText(event.target.value)}
              error={startInvalid}
              helperText={helper(start, startInvalid)}
              inputProps={{ 'aria-label': isRange ? 'In timecode' : 'Timecode', style: TC_INPUT_SX }}
              sx={{ width: 170 }}
            />
            <Button size="small" onClick={() => setStartText(formatTimecode(getPlayhead(), fps))} sx={{ mt: 0.5 }}>
              Use playhead
            </Button>
            {!cutting && (
              <FormControlLabel
                sx={{ ml: 'auto' }}
                control={<Switch checked={isRange} onChange={(event) => setIsRange(event.target.checked)} />}
                label="Range"
              />
            )}
          </Box>

          {!cutting && isRange && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Out"
                size="small"
                value={endText}
                onChange={(event) => setEndText(event.target.value)}
                error={endInvalid}
                helperText={helper(end, endInvalid)}
                inputProps={{ 'aria-label': 'Out timecode', style: TC_INPUT_SX }}
                sx={{ width: 170 }}
              />
              <Button size="small" onClick={() => setEndText(formatTimecode(getPlayhead(), fps))} sx={{ mt: 0.5 }}>
                Use playhead
              </Button>
              {start !== null && end !== null && end > start && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                  Duration {formatDuration(end - start)}
                </Typography>
              )}
            </Box>
          )}

          {(validation || error) && <Alert severity="error">{validation || error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button variant="outlined" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MulticamAnnotationDialog;
